
import React from 'react';
import { useForm } from '../../../context/FormContext';
import { formConfig } from '../../../config/formConfig';
import { FormFieldType } from '@/types/form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { isLateBeyond2Days } from '@/context/form/utils/dateTimeUtils';
import { useTranslation } from 'react-i18next';
import { tField } from '../utils/i18nField';

const ReviewPage: React.FC = () => {
  const { formData } = useForm();
  const { t } = useTranslation();
  
  // Check if submission is late beyond 2 days
  const teachingDate = formData.teaching_date as string;
  const isLateSubmission = isLateBeyond2Days(teachingDate);
  
  // Flatten all form fields from all pages
  const allFields = formConfig.flatMap(page => page.elements);
  
  // Fields to exclude from review page
  const excludedFields = ['agreed_to_terms', 'student_search'];
  
  // Filter out non-input fields and excluded fields
  const inputFields = allFields.filter(field => 
    field.name && 
    !['section_header', 'instruction', 'rich_text_instruction'].includes(field.type) &&
    !excludedFields.includes(field.name)
  );

  // Helper function to format field values for display
  const formatFieldValue = (field: FormFieldType, value: any): React.ReactNode => {
    if (value === undefined || value === null || value === '') return '-';
    
    // Handle different field types
    switch (field.type) {
      case 'checkbox': {
        // Multi-select checkbox group → value is string[]
        if (Array.isArray(value)) {
          if (value.length === 0) return '-';
          const labels = value.map((v) => {
            const opt = field.options?.find((o) => o.value === v);
            return opt ? tField(t, opt.label) : String(v);
          });
          // Append "other" free-text if present
          const otherKey = field.options?.find((o) => (o as any).hasTextInput)?.[
            'textInputName' as keyof typeof field.options[number]
          ] as string | undefined;
          if (otherKey && formData[otherKey]) {
            labels.push(String(formData[otherKey]));
          }
          return labels.join(', ');
        }
        // Single boolean checkbox
        return value === true ? t('form.review.yes') : t('form.review.no');
      }
      case 'radio':
      case 'select':
        // For radio and select, display the selected option label instead of value
        if (field.options) {
          const option = field.options.find(opt => opt.value === value);
          return option ? tField(t, option.label) : String(value);
        }
        return String(value);
      case 'file':
        // For check-in photos, show filenames instead of previews
        if (field.name === 'check_in_photo_urls') {
          if (Array.isArray(value) && value.length > 0) {
            console.log(value)
            return (
              <ul className="list-disc pl-5">
                {value.map((file: File | string, index: number) => (
                  <li key={index}>
                    {typeof file === 'string' 
                      ? file.split('/').pop() // Extract filename from URL
                      : file.name}
                    {typeof file !== 'string' && t('form.review.fileNotFound')}
                  </li>
                ))}
              </ul>
            );
          }
        }
        // For worksheet files, show filenames
        else if (field.name === 'worksheet_urls') {
          if (Array.isArray(value) && value.length > 0) {
            return (
              <ul className="list-disc pl-5">
                {value.map((file: File | string, index: number) => (
                  <li key={index}>
                    {typeof file === 'string' 
                      ? file.split('/').pop() // Extract filename from URL
                      : file.name}
                  </li>
                ))}
              </ul>
            );
          }
        }
        
        // Default file display
        if (Array.isArray(value)) {
          return t('form.review.filesUploaded', { count: value.length });
        }
        return String(value);
      default:
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        return String(value);
    }
  };

  return (
    <div className="space-y-6">
      {/* Alert when submission is late beyond 2 days */}
      {isLateSubmission && (
        <Alert variant="destructive" className="border-destructive bg-destructive/10">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="font-bold">
            {t('form.review.lateTitle')}
          </AlertTitle>
          <AlertDescription>
            <p className="mt-2" dangerouslySetInnerHTML={{ __html: t('form.review.lateDesc1') }} />
            <p className="mt-1" dangerouslySetInnerHTML={{ __html: t('form.review.lateDesc2') }} />
          </AlertDescription>
        </Alert>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('form.review.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/3">{t('form.review.fieldName')}</TableHead>
                <TableHead>{t('form.review.fieldValue')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inputFields.map((field) => (
                field.name && (
                  <TableRow key={field.id}>
                    <TableCell className="font-medium">{field.label ? tField(t, field.label) : field.name}</TableCell>
                    <TableCell>
                      {formatFieldValue(field, formData[field.name])}
                    </TableCell>
                  </TableRow>
                )
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReviewPage;
